import { useQuery } from '@tanstack/react-query';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import type { ObjectiveType } from '@/lib/student-objectives/objective-types';

export interface AdminObjectiveTemplate {
  id: string;
  title: string;
  description: string;
  type: ObjectiveType | string;
  allowed_levels: SchoolLevel[];
  subject: SubjectSlug | null;
  filters: Record<string, unknown>;
  /** L'élève choisit un pack (migration 0514). */
  requires_pack_selection: boolean;
  /** Programme épinglé (migration 0514). */
  programme_id: string | null;
  content_type: 'exercises' | 'epreuves';
  is_active: boolean;
  display_order: number;
  illustration_url: string | null;
  illustration_prompt: string | null;
  default_deadline: string | null;
  created_at: string;
  updated_at: string;
  student_count?: number;
}

export function useAdminObjectiveTemplates() {
  return useQuery({
    queryKey: ['admin-objective-templates'],
    queryFn: async (): Promise<AdminObjectiveTemplate[]> => {
      const { data, error } = await supabase
        .from('objective_templates')
        .select('*, student_objectives(count)')
        .order('display_order');
      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => {
        const r = row as typeof row & {
          illustration_url?: string | null;
          illustration_prompt?: string | null;
          default_deadline?: string | null;
        };
        return {
          ...row,
          allowed_levels: (row.allowed_levels ?? []) as SchoolLevel[],
          filters: (row.filters ?? {}) as Record<string, unknown>,
          content_type: (row.content_type ?? 'exercises') as 'exercises' | 'epreuves',
          illustration_url: r.illustration_url ?? null,
          illustration_prompt: r.illustration_prompt ?? null,
          default_deadline: r.default_deadline ?? null,
          requires_pack_selection:
            (r as { requires_pack_selection?: boolean }).requires_pack_selection ?? false,
          programme_id: (r as { programme_id?: string | null }).programme_id ?? null,
          student_count:
            (row.student_objectives as unknown as { count: number }[])?.[0]?.count ?? 0,
        };
      });
    },
  });
}

export function useAdminObjectiveTemplate(id: string) {
  return useQuery({
    queryKey: ['admin-objective-template', id],
    enabled: !!id,
    queryFn: async (): Promise<AdminObjectiveTemplate | null> => {
      const { data, error } = await supabase
        .from('objective_templates')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      const d = data as typeof data & {
        illustration_url?: string | null;
        illustration_prompt?: string | null;
        default_deadline?: string | null;
      };
      return {
        ...data,
        allowed_levels: (data.allowed_levels ?? []) as SchoolLevel[],
        filters: (data.filters ?? {}) as Record<string, unknown>,
        content_type: (data.content_type ?? 'exercises') as 'exercises' | 'epreuves',
        illustration_url: d.illustration_url ?? null,
        illustration_prompt: d.illustration_prompt ?? null,
        default_deadline: d.default_deadline ?? null,
        requires_pack_selection:
          (d as { requires_pack_selection?: boolean }).requires_pack_selection ?? false,
        programme_id: (d as { programme_id?: string | null }).programme_id ?? null,
      };
    },
  });
}
